import type { ThemeConfig } from "antd";
import { theme } from "antd";

export const lightTheme = {
  token: {
    lineWidthFocus: 0,
    fontFamily: "Inter",
    borderRadius: 4,
    colorPrimary: "var(--kds-accent-fg)",
    colorPrimaryBg: "var(--kds-accent-soft)",
    colorInfo: "var(--kds-accent-fg)",
    colorInfoBg: "var(--kds-accent-soft)",
    colorSuccess: "var(--kds-success-fg)",
    colorSuccessBg: "var(--kds-success-soft)",
    colorWarning: "var(--kds-warning-fg)",
    colorWarningBg: "var(--kds-warning-soft)",
    colorError: "var(--kds-danger-fg)",
    colorErrorBg: "var(--kds-danger-soft)",
    colorTextBase: "#222222",
    wireframe: false,
    boxShadow: "0px 2px 4px 0px rgba(34, 34, 34, 0.10)",
    boxShadowSecondary: "0px 2px 4px 0px rgba(34, 34, 34, 0.10)",
    motionDurationMid: "0s",
  },
  components: {
    Progress: {
      colorPrimary: "#0191f8",
      colorText: "#ececec",
      remainingColor: "var(--kds-light-reverse)",
    },
    Pagination: {
      algorithm: true,
      itemActiveBg: "#0191f8",
      colorPrimary: "rgb(255, 255, 255)",
      margin: 24,
    },
    Checkbox: {
      borderRadiusSM: 2,
      colorBorder: "var(--kds-border)",
      colorBgContainer: "#ffffff",
      colorPrimary: "var(--kds-accent)",
      colorPrimaryHover: "var(--kds-accent)",
      colorWhite: "var(--kds-light-reverse)",
    },
    Dropdown: {
      controlItemBgHover: "rgb(238, 246, 253)",
      paddingBlock: 4,
      borderRadiusLG: 8,
    },
    Input: {
      algorithm: true,
      colorBorder: "var(--kds-border-subtle)",
      activeShadow: "none",
      activeBorderColor: "#0191f8",
      activeBg: "var(--kds-bg-element)",
      hoverBg: "var(--kds-bg-element)",
      hoverBorderColor: "var(--kds-border-subtle)",
      colorBgContainerDisabled: "var(--palette-slate-4)",
      colorBgContainer: "var(--kds-bg-element)",
      colorTextPlaceholder: "var(--kds-muted)",
      colorText: "var(--kds-fg)",
      colorTextDisabled: "var(--kds-muted)",
      colorError: "var(--kds-danger-fg)",
      colorErrorBorderHover: "var(--kds-danger)",
      paddingBlock: 4,
      paddingBlockLG: 12,
      paddingInline: 12,
      paddingInlineLG: 12,
      borderRadius: 8,
      borderRadiusLG: 12,
    },
    Radio: {
      buttonPaddingInline: 8,
      colorPrimaryHover: "#0191f8",
      paddingXS: 4,
      controlHeight: 34,
      colorPrimaryBorder: "#0191f8",
      colorPrimary: "#0191f8",
      colorText: "rgb(153, 153, 153)",
      colorTextDisabled: "rgb(187, 187, 187)",
      colorBorder: "rgb(228, 228, 228)",
      colorBgContainerDisabled: "rgb(245, 245, 245)",
    },
    Table: {
      cellPaddingInline: 12,
      cellPaddingBlock: 12,
      rowHoverBg: "rgb(249, 251, 252)",
      bodySortBg: "rgb(255, 255, 255)",
      headerBg: "rgb(245, 246, 248)",
      headerSortHoverBg: "rgb(245, 246, 248)",
      headerSortActiveBg: "rgb(245, 246, 248)",
      colorText: "rgb(34, 34, 34)",
      cellPaddingInlineMD: 14,
      cellPaddingInlineSM: 14,
      headerBorderRadius: 10,
    },
    Button: {
      algorithm: true,
      paddingInline: 12,
      paddingBlock: 2,
      colorBorder: "rgb(228, 228, 228)",
      colorPrimaryHover: "#0191f8",
    },
    Select: {
      // optionActiveBg: "rgb(238, 246, 253)",
      // optionSelectedBg: "rgb(238, 246, 253)",
      controlOutlineWidth: 0,
      selectorBg: "var(--kds-bg-element)",
      colorBorder: "var(--kds-border)",
      colorText: "var(--kds-fg)",
      colorTextPlaceholder: "var(--kds-muted)",
      colorTextDisabled: "var(--kds-muted)",
      colorTextSelected: "var(--kds-fg)",
      colorTextSelectedDisabled: "var(--kds-muted)",
      colorTextSelectedHover: "var(--kds-fg)",
      colorTextSelectedActive: "var(--kds-fg)",
      optionActiveBg: "var(--kds-bg-overlay)",
      optionSelectedBg: "var(--kds-accent-soft)",
      hoverBorderColor: "var(--kds-accent)",
      activeBorderColor: "var(--kds-accent)",
      colorBorderDisabled: "var(--kds-border)",
      fontSizeLG: 14,
      borderRadius: 8,
      borderRadiusLG: 8,
    },
    Message: {
      algorithm: true,
      colorSuccess: "var(--kds-success-fg)",
      colorError: "var(--kds-danger-fg)",
      colorWarning: "var(--kds-warning-fg)",
      colorInfo: "var(--kds-accent-fg)",
      colorText: "rgb(34, 34, 34)",
      contentBg: "rgb(255, 255, 255)",
    },
    Segmented: {
      trackBg: "var(--kds-bg-soft)",
      itemActiveBg: "var(--kds-bg-element)",
      itemHoverBg: "var(--kds-bg-element)",
      itemColor: "var(--kds-fg-soft)",
      itemSelectedBg: "var(--kds-bg-element)",
      itemSelectedColor: "var(--kds-fg)",
      itemHoverColor: "var(--kds-fg)",
    },
    Modal: {
      motionDurationSlow: "0.01s",
      contentBg: "var(--kds-bg)",
      titleColor: "var(--kds-fg)",
      borderRadiusLG: 12,
      headerMarginBottom: 16,
      footerMarginTop: 16,
      contentPadding: "16px 24px",
    },
    InputNumber: {
      hoverBorderColor: "var(--kds-border)",
      activeBorderColor: "var(--kds-border)",
      activeShadow: "none",
      handleHoverColor: "var(--kds-fg)",
    },
    Tooltip: {
      colorBgSpotlight: "var(--kds-bg-card)",
      colorTextLightSolid: "var(--kds-fg)",
    },
    Popover: {
      colorBgElevated: "var(--kds-bg-card)",
      colorText: "var(--kds-fg)",
    },
    Divider: {
      colorSplit: "#eeeeee",
    },
    Badge: {
      colorBgContainer: "#131313",
      colorText: "#ffffff",
      colorError: "var(--kds-danger)",
      textFontSizeSM: 10,
    },
    Tag: {
      colorError: "var(--kds-danger-fg)",
      colorErrorBg: "var(--kds-danger-soft)",
      colorWarning: "var(--kds-warning-fg)",
      colorWarningBg: "var(--kds-warning-soft)",
      colorSuccess: "var(--kds-success-fg)",
      colorSuccessBg: "var(--kds-success-soft)",
      colorInfo: "var(--kds-accent-fg)",
      colorInfoBg: "var(--kds-accent-soft)",
      colorPrimary: "var(--kds-accent-fg)",
      colorPrimaryBg: "var(--kds-accent-soft)",
    },
    Switch: {
      colorPrimary: "var(--kds-accent)",
      colorPrimaryHover: "var(--kds-accent)",
      colorTextQuaternary: "rgb(228, 228, 228)",
      colorTextTertiary: "rgba(228, 228, 228, 0.8)",
      handleSize: 20,
      trackHeight: 24,
      trackPadding: 2,
      handleShadow: "none",
    },
    Alert: {
      colorInfoBg: "var(--kds-accent-soft)",
      colorSuccessBg: "var(--kds-success-soft)",
      colorWarningBg: "var(--kds-warning-soft)",
      colorErrorBg: "var(--kds-danger-soft)",
    },
    DatePicker: {
      activeShadow: "none",
      hoverBorderColor: "var(--kds-border)",
      handleHoverColor: "var(--kds-fg)",
      activeBorderColor: "#0191f8",
    },
  },
} as ThemeConfig;

export const darkTheme = {
  token: {
    lineWidthFocus: 0,
    fontFamily: "Inter",
    borderRadius: 4,
    colorPrimary: "var(--kds-accent-fg)",
    colorPrimaryBg: "var(--kds-accent-soft)",
    colorInfo: "var(--kds-accent-fg)",
    colorInfoBg: "var(--kds-accent-soft)",
    colorSuccess: "var(--kds-success-fg)",
    colorSuccessBg: "var(--kds-success-soft)",
    colorWarning: "var(--kds-warning-fg)",
    colorWarningBg: "var(--kds-warning-soft)",
    colorError: "var(--kds-danger-fg)",
    colorErrorBg: "var(--kds-danger-soft)",
    colorTextBase: "#f2f2f2",
    colorBgBase: "#131415",
    colorBgContainer: "#131313",
    colorBgElevated: "var(--kds-bg-card)",
    colorBgLayout: "#191b1e",
    colorBorder: "#3b3b3b",
    colorBorderSecondary: "#3b3b3b",
    colorText: "#ececec",
    colorTextSecondary: "#cfcfcf",
    colorTextTertiary: "#aeaeae",
    colorTextQuaternary: "#aeaeae",
    colorFill: "#3b3b3b",
    colorFillSecondary: "#3b3b3b",
    colorFillTertiary: "#3b3b3b",
    colorFillQuaternary: "#3b3b3b",
    wireframe: false,
    boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.3)",
    boxShadowSecondary: "0px 2px 4px 0px rgba(0, 0, 0, 0.3)",
    motionDurationSlow: "0.01s",
    motionDurationMid: "0.01s",
  },
  components: {
    Pagination: {
      algorithm: true,
      itemActiveBg: "#0191f8",
      colorPrimary: "rgb(255, 255, 255)",
      colorBgContainer: "#131313",
      colorText: "#ececec",
      margin: 24,
    },
    Checkbox: {
      borderRadiusSM: 2,
      colorBorder: "rgba(230, 230, 230, 0.12)",
      colorBgContainer: "rgba(230, 230, 230, 0.12)",
      colorPrimary: "var(--kds-fg)",
      colorPrimaryHover: "var(--kds-fg)",
      colorWhite: "var(--kds-light-reverse)",
    },
    Dropdown: {
      controlItemBgHover: "rgba(1, 145, 248, 0.1)",
      paddingBlock: 4,
      colorBgElevated: "var(--kds-bg-card)",
      colorText: "#ececec",
      borderRadiusLG: 8,
    },
    Input: {
      algorithm: true,
      activeBg: "var(--kds-bg-element)",
      activeBorderColor: "rgba(255, 255, 255, 0)",
      addonBg: "rgba(255, 255, 255, 0)",
      hoverBg: "var(--kds-bg-element)",
      hoverBorderColor: "var(--kds-border-subtle)",
      colorBorder: "var(--kds-border-subtle)",
      colorBgContainer: "var(--kds-bg-element)",
      colorBgContainerDisabled: "var(--palette-slate-4)",
      colorTextPlaceholder: "var(--kds-muted)",
      colorText: "var(--kds-fg)",
      colorTextDisabled: "var(--kds-muted)",
      colorError: "var(--kds-danger)",
      colorErrorBorderHover: "var(--kds-danger)",
      paddingBlock: 4,
      paddingBlockLG: 12,
      paddingInline: 12,
      paddingInlineLG: 12,
      activeShadow: "none",
      errorActiveShadow: "none",
      warningActiveShadow: "none",
      borderRadius: 8,
      borderRadiusLG: 12,
    },
    Radio: {
      buttonPaddingInline: 8,
      colorPrimaryHover: "#0191f8",
      paddingXS: 4,
      controlHeight: 34,
      colorPrimaryBorder: "#0191f8",
      colorPrimary: "#0191f8",
      colorText: "#cfcfcf",
      colorTextDisabled: "#aeaeae",
      colorBorder: "#3b3b3b",
      colorBgContainerDisabled: "#1b1b1b",
      colorBgContainer: "#131313",
    },
    Table: {
      cellPaddingInline: 12,
      cellPaddingBlock: 12,
      rowHoverBg: "rgba(1, 145, 248, 0.05)",
      bodySortBg: "rgb(19, 20, 21)",
      headerBg: "rgba(255, 255, 255, 0.08)",
      colorBgContainer: "#131313",
      colorBorderSecondary: "#3b3b3b",
      headerColor: "rgb(242, 242, 243)",
      headerFilterHoverBg: "rgba(255, 255, 255, 0.08)",
      headerSortActiveBg: "rgba(255, 255, 255, 0.08)",
      borderColor: "rgba(255, 255, 255, 0.08)",
      cellPaddingInlineMD: 14,
      cellPaddingInlineSM: 14,
      headerBorderRadius: 10,
      headerSplitColor: "rgba(0, 0, 0, 0)",
      footerBg: "rgb(19, 20, 21)",
    },
    Button: {
      algorithm: true,
      paddingInline: 12,
      paddingBlock: 2,
      colorBorder: "#3b3b3b",
      colorPrimaryBgHover: "rgb(255, 255, 255)",
      colorPrimaryBg: "rgb(230, 230, 230)",
      colorPrimary: "rgb(230, 230, 230)",
      dangerShadow: "none",
      defaultShadow: "none",
      primaryShadow: "none",
      colorPrimaryActive: "rgb(230, 230, 230)",
      primaryColor: "rgb(19, 20, 21)",
      colorPrimaryBorder: "rgba(255, 255, 255, 0)",
      colorPrimaryHover: "rgb(255, 255, 255)",
      defaultBgDisabled: "rgba(255, 255, 255, 0.08)",
      colorTextDisabled: "rgba(255, 255, 255, 0.3)",
      fontWeight: 500,
      opacityLoading: 1,
      defaultBg: "rgba(230, 230, 230, 0.12)",
      defaultBorderColor: "rgba(230, 230, 230, 0.12)",
      defaultActiveBg: "rgba(255, 255, 255, 0.16)",
      defaultActiveBorderColor: "rgba(255, 255, 255, 0.16)",
      defaultColor: "rgb(242, 242, 243)",
      defaultHoverBg: "rgba(255, 255, 255, 0.16)",
      defaultHoverBorderColor: "rgba(255, 255, 255, 0.16)",
      defaultHoverColor: "rgb(230, 230, 230)",
      colorText: "rgb(242, 242, 243)",
      defaultActiveColor: "rgb(242, 242, 243)",
      defaultGhostBorderColor: "#7A7A85",
    },
    Select: {
      // optionActiveBg: "rgba(1, 145, 248, 0.1)",
      // optionSelectedBg: "rgba(1, 145, 248, 0.1)",
      // colorBgElevated: "var(--kds-bg-card)",
      // colorText: "#ececec",
      // colorBgContainer: "rgba(255, 255, 255, 0.08)",
      // colorBorder: "rgba(255, 255, 255, 0)",
      // colorError: "rgb(255, 255, 255)",
      controlOutlineWidth: 0,
      selectorBg: "var(--kds-bg-element)",
      colorBorder: "var(--kds-border)",
      colorText: "var(--kds-fg)",
      colorTextPlaceholder: "var(--kds-muted)",
      colorTextDisabled: "var(--kds-muted)",
      colorTextSelected: "var(--kds-fg)",
      colorTextSelectedDisabled: "var(--kds-muted)",
      colorTextSelectedHover: "var(--kds-fg)",
      colorTextSelectedActive: "var(--kds-fg)",
      optionActiveBg: "var(--kds-bg-overlay)",
      optionSelectedBg: "var(--kds-accent-soft)",
      colorBorderDisabled: "var(--kds-border)",
      fontSizeLG: 14,
      borderRadius: 8,
      borderRadiusLG: 8,
    },
    Message: {
      colorError: "var(--kds-danger-fg)",
      colorWarning: "var(--kds-warning-fg)",
      colorText: "#ececec",
      colorBgElevated: "var(--kds-bg-card)",
      colorSuccess: "var(--kds-success-fg)",
      colorInfo: "var(--kds-accent-fg)",
      contentBg: "rgb(64, 64, 64)",
    },
    Modal: {
      motionDurationSlow: "0.01s",
      colorBgElevated: "var(--kds-bg-card)",
      colorText: "#ececec",
      contentBg: "var(--kds-bg)",
      titleColor: "var(--kds-fg)",
      borderRadiusLG: 12,
      headerMarginBottom: 16,
      footerMarginTop: 16,
      contentPadding: "16px 24px",
    },
    InputNumber: {
      hoverBorderColor: "var(--kds-border)",
      activeBorderColor: "var(--kds-border)",
      activeShadow: "none",
      handleHoverColor: "var(--kds-fg)",
    },
    Card: {
      colorBgContainer: "#131313",
      colorBorderSecondary: "#3b3b3b",
      colorText: "#ececec",
    },
    Drawer: {
      colorBgElevated: "var(--kds-bg-card)",
      colorText: "#ececec",
    },
    Tooltip: {
      colorBgSpotlight: "var(--kds-bg-card)",
      colorTextLightSolid: "var(--kds-fg)",
    },
    Popover: {
      colorBgElevated: "var(--kds-bg-card)",
      colorText: "var(--kds-fg)",
    },
    DatePicker: {
      activeShadow: "none",
      colorBgContainer: "#131313",
      colorBgElevated: "var(--kds-bg-card)",
      colorText: "#ececec",
      colorBorder: "#3b3b3b",
    },
    TimePicker: {
      colorBgContainer: "#131313",
      colorBgElevated: "var(--kds-bg-card)",
      colorText: "#ececec",
      colorBorder: "#3b3b3b",
    },
    Switch: {
      colorPrimary: "var(--kds-accent)",
      colorPrimaryHover: "var(--kds-accent)",
      colorTextQuaternary: "#aeaeae",
      handleBg: "rgb(230, 230, 230)",
      colorPrimaryBorder: "var(--kds-accent)",
      colorText: "rgb(242, 242, 243)",
      handleSize: 20,
      trackHeight: 24,
      trackPadding: 2,
      handleShadow: "none",
    },
    Slider: {
      colorPrimary: "#0191f8",
      colorTextQuaternary: "#aeaeae",
    },
    Progress: {
      colorPrimary: "#0191f8",
      colorText: "#ececec",
      remainingColor: "var(--kds-light-reverse)",
    },
    Tabs: {
      colorText: "#cfcfcf",
      colorTextSecondary: "#aeaeae",
      colorBorderSecondary: "#3b3b3b",
      colorFillSecondary: "#3b3b3b",
    },
    Menu: {
      colorBgContainer: "#131313",
      colorText: "#ececec",
      itemSelectedBg: "rgba(1, 145, 248, 0.1)",
      itemHoverBg: "rgba(1, 145, 248, 0.05)",
    },
    Breadcrumb: {
      colorText: "#cfcfcf",
      colorTextDescription: "#aeaeae",
    },
    Steps: {
      colorText: "#ececec",
      colorTextDescription: "#cfcfcf",
    },
    Alert: {
      colorInfoBg: "var(--kds-accent-soft)",
      colorSuccessBg: "var(--kds-success-soft)",
      colorWarningBg: "var(--kds-warning-soft)",
      colorErrorBg: "var(--kds-danger-soft)",
      colorText: "#ececec",
    },
    Badge: {
      colorBgContainer: "#131313",
      colorText: "#ffffff",
      colorError: "var(--kds-danger)",
      textFontSizeSM: 10,
    },
    Tag: {
      colorError: "var(--kds-danger-fg)",
      colorErrorBg: "var(--kds-danger-soft)",
      colorWarning: "var(--kds-warning-fg)",
      colorWarningBg: "var(--kds-warning-soft)",
      colorSuccess: "var(--kds-success-fg)",
      colorSuccessBg: "var(--kds-success-soft)",
      colorInfo: "var(--kds-accent-fg)",
      colorInfoBg: "var(--kds-accent-soft)",
      colorPrimary: "var(--kds-accent-fg)",
      colorPrimaryBg: "var(--kds-accent-soft)",
      colorBgContainer: "#1b1b1b",
      colorText: "#ececec",
      defaultBg: "rgba(255, 255, 255, 0.08)",
      defaultColor: "rgb(218, 218, 218)",
    },
    Cascader: {
      colorBgElevated: "var(--kds-bg-card)",
      colorText: "#ececec",
      colorBorder: "#3b3b3b",
    },
    TreeSelect: {
      colorBgElevated: "var(--kds-bg-card)",
      colorText: "#ececec",
      colorBorder: "#3b3b3b",
    },
    Transfer: {
      colorBgContainer: "#131313",
      colorText: "#ececec",
      colorBorder: "#3b3b3b",
    },
    Upload: {
      colorBgContainer: "#131313",
      colorText: "#ececec",
      colorBorder: "#3b3b3b",
    },
    Form: {
      colorText: "#ececec",
      colorTextSecondary: "#cfcfcf",
    },
    Spin: {
      colorPrimary: "#0191f8",
    },
    Skeleton: {
      colorFill: "#3b3b3b",
      colorFillContent: "#1b1b1b",
    },
    Anchor: {
      colorText: "#cfcfcf",
      colorTextSecondary: "#aeaeae",
    },
    BackTop: {
      colorBgContainer: "#131313",
      colorText: "#ececec",
    },
    Affix: {
      colorBgContainer: "#131313",
    },
    ConfigProvider: {
      colorBgContainer: "#131313",
      colorText: "#ececec",
    },
    Divider: {
      colorSplit: "rgba(254, 254, 254, 0.08)",
    },
    Segmented: {
      trackBg: "var(--kds-bg-soft)",
      itemActiveBg: "var(--kds-dark-reverse)",
      itemHoverBg: "var(--kds-dark-reverse)",
      itemColor: "var(--kds-fg-soft)",
      itemSelectedBg: "var(--kds-dark-reverse)",
      itemSelectedColor: "var(--kds-light-reverse)",
      itemHoverColor: "var(--kds-light-reverse)",
    },
  },
  algorithm: theme.darkAlgorithm,
} as ThemeConfig;
